/**
 * Common utility functions for object validation and manipulation
 * These utilities follow functional programming principles and are designed to be pure functions
 */

import { CONVERSATIONAL_ENDPOINTS } from "@/constants/constants";
import { getBrandIdUnderscoreFormat } from "../brand.utils";

/**
 * Checks if an object has any enumerable keys
 * @param obj - The object to check
 * @returns true if the object has keys, false otherwise
 */
export function hasKeys(obj: unknown): obj is Record<string, unknown> {
  return typeof obj === "object" && obj !== null && !Array.isArray(obj) && Object.keys(obj).length > 0;
}

/**
 * Checks if an object is empty (has no enumerable keys)
 * @param obj - The object to check
 * @returns true if the object is empty, false otherwise
 */
export function isEmptyObject(obj: unknown): boolean {
  return !hasKeys(obj);
}

/**
 * Enhanced utility to check if an object has empty values
 * This function checks if an object exists but all its values are empty
 * (null, undefined, empty strings after trimming, or empty arrays)
 *
 * @param obj - The object to check
 * @returns true if object exists but all values are empty, false otherwise
 */
export function hasEmptyValues(obj: unknown): boolean {
  if (!hasKeys(obj)) {
    return false; // Object doesn't exist or has no keys
  }

  return Object.values(obj).every((value) => {
    if (!value) return true; // null, undefined, 0, false, etc.
    if (typeof value === "string") return value.trim() === "";
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "object") return isEmptyObject(value);
    return false;
  });
}

/**
 * Comprehensive utility to check if cached RAI error object should be considered empty
 * This handles the specific use case where:
 * 1. No cached RAI error object exists, OR
 * 2. Cached RAI error object exists but all values are empty strings or empty arrays
 *
 * @param cachedRaiError - The cached RAI error object to check
 * @returns true if should be considered empty, false otherwise
 */
export function isEmptyCachedRaiError(cachedRaiError: unknown): boolean {
  return isEmptyObject(cachedRaiError) || hasEmptyValues(cachedRaiError);
}

/**
 * Utility to safely get object values with type checking
 * @param obj - The object to get values from
 * @returns Array of values or empty array if object is invalid
 */
export function safeObjectValues<T>(obj: unknown): T[] {
  if (!hasKeys(obj)) {
    return [];
  }
  return Object.values(obj) as T[];
}

/**
 * Utility to safely get object keys with type checking
 * @param obj - The object to get keys from
 * @returns Array of keys or empty array if object is invalid
 */
export function safeObjectKeys(obj: unknown): string[] {
  if (!hasKeys(obj)) {
    return [];
  }
  return Object.keys(obj);
}

/**
 * Utility to check if a value is a non-empty string after trimming
 * @param value - The value to check
 * @returns true if value is a non-empty string after trimming
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * Utility to check if a value is a non-empty array
 * @param value - The value to check
 * @returns true if value is a non-empty array
 */
export function isNonEmptyArray<T>(value: unknown): value is T[] {
  return Array.isArray(value) && value.length > 0;
}

export const isConversationalEndpoint = (endpoint: string): boolean => CONVERSATIONAL_ENDPOINTS.includes(endpoint);

/**
 * Parses a combined brand+location string into its constituent parts.
 *
 * Supported formats (evaluated in priority order):
 *  1. Underscore-delimited  → "oreo_be_us"   → ["oreo be", "us"]
 *  2. Spaced-hyphen         → "oreo be - us" → ["oreo be", "us"]
 *  3. Bare-hyphen (last)    → "oreo be-us"   → ["oreo be", "us"]
 *
 * @param input - Raw brand+location string from the data source.
 * @returns A named tuple [brandName, brandLocation]; both values are trimmed.
 *          Returns ["", ""] for empty/whitespace-only input.
 */
export function getBrandAndLocation(input: string): [brandName: string, brandLocation: string] {
  const normalized = input?.trim();
  if (!normalized) return ["", ""];

  // Priority 1 — underscore delimiter: "oreo_be_us" → ["oreo be", "us"]
  if (normalized.includes("_")) {
    // e.g. "oreo_be_us" → brandName: "oreo be", brandLocation: "us"
    const parts = normalized.split("_");
    const brandLocation = parts.at(-1) ?? "";
    const brandName = parts.slice(0, -1).join(" ");
    return [brandName.trim(), brandLocation.trim()];
  }

  // Priority 2 — spaced-hyphen delimiter: "oreo be - us" → ["oreo be", "us"]
  const spacedHyphen = normalized.indexOf(" - ");
  if (spacedHyphen !== -1) {
    const brandName = normalized.slice(0, spacedHyphen);
    const brandLocation = normalized.slice(spacedHyphen + 3); // 3 = " - ".length
    return [brandName.trim(), brandLocation.trim()];
  }

  // Priority 3 — bare-hyphen (last occurrence): "oreo be-us" → ["oreo be", "us"]
  const lastHyphen = normalized.lastIndexOf("-");
  if (lastHyphen === -1) return [normalized, ""];

  const brandName = normalized.slice(0, lastHyphen);
  const brandLocation = normalized.slice(lastHyphen + 1);
  return [brandName.trim(), brandLocation.trim()];
}

export const getBrandKey = (brand: string, location: string, id?: string): string =>
  id?.includes("_") ? getBrandIdUnderscoreFormat(brand, location) : `${brand} - ${location}`;
